/* Admin — Zip Manager: 100% manual per-offer zip management.
   No scraping, no uploads, no scheduler. Admin creates offers by hand
   and pastes a block of zip codes for each. */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminShell, { StatCard } from "../components/admin/AdminShell";
import { authFetch } from "../utils/adminAuth";
import {
  MapPin,
  Loader2,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  Copy,
  ListChecks,
  Facebook,
  Search as GoogleIcon,
  Plus,
  Trash2,
  Pencil,
  X,
  Clock,
} from "lucide-react";

const call = async (path, opts = {}) => {
  const headers = { ...(opts.headers || {}) };
  if (!headers["Content-Type"] && opts.body && typeof opts.body === "string") {
    headers["Content-Type"] = "application/json";
  }
  const res = await authFetch(path, { ...opts, headers });
  const ct = res.headers.get("content-type") || "";
  const body = ct.includes("application/json") ? await res.json() : await res.text();
  if (!res.ok) {
    const msg = typeof body === "string" ? body : (body?.detail || `HTTP ${res.status}`);
    throw new Error(msg);
  }
  return body;
};

const fmtDate = (iso) => {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
};

const CATEGORIES = [
  { key: "facebook", label: "Facebook Marketplace", icon: Facebook,
    swatch: "from-blue-600 to-sky-500", ring: "ring-blue-500/40",
    text: "text-blue-300", bg: "bg-blue-500/10", border: "border-blue-500/30" },
  { key: "google", label: "Google Ads", icon: GoogleIcon,
    swatch: "from-amber-500 to-orange-500", ring: "ring-amber-500/40",
    text: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30" },
];
const catByKey = (k) => CATEGORIES.find((c) => c.key === k) || CATEGORIES[0];

// Client-side echo of the backend parser so the offer form can show a live
// count while the admin types/pastes. Returns {unique, duplicates} counts.
const previewZipCount = (text) => {
  if (!text) return { unique: 0, duplicates: 0 };
  const seen = new Set();
  let raw = 0;
  const bump = (z) => { raw += 1; seen.add(z); };
  for (const rawTok of String(text).split(/[\s,;|]+/)) {
    const t = rawTok.trim();
    if (!t) continue;
    const m5 = /^(\d{5})(?:-\d{4})?$/.exec(t);
    if (m5) { bump(m5[1]); continue; }
    if (/^\d{4}$/.test(t)) { bump("0" + t); continue; }
    for (const m of t.matchAll(/\b(\d{5})(?:-\d{4})?\b/g)) bump(m[1]);
  }
  return { unique: seen.size, duplicates: Math.max(0, raw - seen.size) };
};

export default function AdminZipManagerPage() {
  const [category, setCategory] = useState("facebook");
  const [meta, setMeta] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const cat = catByKey(category);

  const load = useCallback(async (nextCat) => {
    const eff = nextCat || category;
    setLoading(true);
    setError("");
    try {
      const data = await call(`/api/admin/zip-manager/status?category=${eff}`);
      setMeta(data.meta || null);
      setOffers(data.offers || []);
    } catch (e) { setError(e.message || "Failed to load"); }
    finally { setLoading(false); }
  }, [category]);

  useEffect(() => { load(); }, [load]);

  const switchCategory = (k) => {
    if (k === category) return;
    setCategory(k);
    setSuccess(""); setError("");
    load(k);
  };

  const startCreate = () => { setEditingId(null); setEditorOpen(true); };
  const startEdit = (id) => { setEditingId(id); setEditorOpen(true); };

  const closeEditor = (msg) => {
    setEditorOpen(false);
    setEditingId(null);
    if (msg) setSuccess(msg);
    load();
  };

  const removeOffer = async (id, name) => {
    if (!window.confirm(`Delete offer "${name}"? This can't be undone.`)) return;
    try {
      await call(`/api/admin/zip-manager/offers/${id}`, { method: "DELETE" });
      setSuccess(`Deleted "${name}".`);
      await load();
    } catch (e) { setError(e.message || "Delete failed"); }
  };

  const catMeta = (meta?.categories || {})[category] || {};
  const totalZips = offers.reduce((s, o) => s + (o.zip_count || 0), 0);

  return (
    <AdminShell
      pageTitle="Zip Manager"
      pageDescription="Manually create offers and paste their target zips — 100% under your control."
      rightSlot={
        <Link
          to="/target-zips"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="zip-manager-view-public"
          className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-slate-300 hover:text-white px-3 py-1.5 rounded-md border border-slate-700 hover:border-slate-500 bg-slate-900/60 transition"
        >
          <ExternalLink className="w-3.5 h-3.5" /> View public page
        </Link>
      }
    >
      {/* Category tabs */}
      <div className="mb-4">
        <p className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">Traffic Source</p>
        <div className="inline-flex rounded-xl border border-slate-800 bg-slate-900/60 p-1 gap-1" role="tablist">
          {CATEGORIES.map((c) => {
            const Icon = c.icon;
            const active = c.key === category;
            const stat = (meta?.categories || {})[c.key];
            return (
              <button
                key={c.key} type="button" role="tab" aria-selected={active}
                onClick={() => switchCategory(c.key)}
                data-testid={`zip-manager-cat-${c.key}`}
                className={`relative inline-flex items-center gap-2 px-4 py-2 rounded-lg text-[13px] font-semibold transition-all ${
                  active ? `bg-gradient-to-r ${c.swatch} text-white shadow-lg ${c.ring} ring-1`
                         : "text-slate-400 hover:text-white hover:bg-slate-800/60"
                }`}
              >
                <Icon className="w-4 h-4" strokeWidth={2.25} />
                {c.label}
                {stat?.offer_count > 0 && (
                  <span className={`ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-md ${active ? "bg-white/20 text-white" : `${c.bg} ${c.text} border ${c.border}`}`}>
                    {stat.offer_count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
        <StatCard testId="zip-stat-offers" label={`${cat.label} · Offers`}
          value={loading ? "…" : offers.length} icon={ListChecks} tone={cat.key === "facebook" ? "info" : "warning"} />
        <StatCard testId="zip-stat-zips" label={`${cat.label} · Total Zips`}
          value={loading ? "…" : totalZips.toLocaleString()} icon={MapPin} tone="success" />
        <StatCard testId="zip-stat-updated" label={`${cat.label} · Last Updated`}
          value={loading ? "…" : (catMeta?.updated_at ? fmtDate(catMeta.updated_at) : "Never")}
          icon={Clock} tone="violet" />
      </div>

      {/* Errors / success */}
      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-300" data-testid="zip-manager-error">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span className="break-words">{error}</span>
        </div>
      )}
      {success && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-[12.5px] text-emerald-300" data-testid="zip-manager-success">
          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {/* Offers table */}
      <div className="rounded-xl border border-slate-800/80 bg-slate-900/60 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-800/70 flex items-center justify-between">
          <h2 className="text-[13px] font-bold text-white tracking-tight flex items-center gap-2">
            Offers in <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold ${cat.bg} ${cat.text} border ${cat.border}`}>
              <cat.icon className="w-3 h-3" /> {cat.label}
            </span>
          </h2>
          <button
            type="button" onClick={startCreate} data-testid="zip-manager-add-offer"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r ${cat.swatch} hover:brightness-110 text-white text-[12.5px] font-bold shadow-lg transition`}
          >
            <Plus className="w-3.5 h-3.5" /> Add New Offer
          </button>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center text-slate-500 text-[13px]">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : offers.length === 0 ? (
          <div className="p-10 text-center">
            <MapPin className="w-8 h-8 mx-auto text-slate-600 mb-2" />
            <p className="text-[13px] text-slate-400">
              No {cat.label} offers yet. Click <b>Add New Offer</b> to create your first one.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-slate-800/70" data-testid="zip-manager-offer-list">
            {offers.map((o) => (
              <div key={o.id} className="flex items-center justify-between px-5 py-3 hover:bg-slate-900/80 transition">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-8 h-8 rounded-md ${cat.bg} border ${cat.border} flex items-center justify-center flex-shrink-0`}>
                    <MapPin className={`w-4 h-4 ${cat.text}`} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[13.5px] font-semibold text-white truncate">{o.offer_name}</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      {o.zip_count?.toLocaleString()} zip codes · updated {fmtDate(o.updated_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <PreviewButton offerName={o.offer_name} category={category} />
                  <button type="button" onClick={() => startEdit(o.id)}
                    data-testid={`zip-edit-${o.offer_name}`}
                    className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition" title="Edit">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => removeOffer(o.id, o.offer_name)}
                    data-testid={`zip-delete-${o.offer_name}`}
                    className="p-1.5 rounded-md text-slate-500 hover:text-red-300 hover:bg-red-500/10 transition" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editorOpen && (
        <OfferEditor
          offerId={editingId}
          category={category}
          cat={cat}
          onClose={closeEditor}
        />
      )}
    </AdminShell>
  );
}


/* Modal — create or edit a single offer. Owns its own state, including the
   giant zip paste. Uses a plain textarea so 50k-zip pastes stay smooth. */
function OfferEditor({ offerId, category, cat, onClose }) {
  const isEdit = Boolean(offerId);
  const [name, setName] = useState("");
  const [zipPaste, setZipPaste] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!isEdit) return;
    let alive = true;
    (async () => {
      try {
        const data = await call(`/api/admin/zip-manager/offers/${offerId}`);
        if (!alive) return;
        setName(data.offer_name || "");
        setZipPaste((data.zip_codes || []).join("\n"));
      } catch (e) {
        if (alive) setErr(e.message || "Failed to load offer");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [isEdit, offerId]);

  const { unique: zipCount, duplicates: dupCount } = previewZipCount(zipPaste);

  const save = async () => {
    setErr("");
    const trimmed = name.trim();
    if (!trimmed) { setErr("Offer name is required."); return; }
    setSaving(true);
    try {
      const path = isEdit
        ? `/api/admin/zip-manager/offers/${offerId}`
        : "/api/admin/zip-manager/offers";
      const method = isEdit ? "PATCH" : "POST";
      const body = isEdit
        ? JSON.stringify({ offer_name: trimmed, zip_paste: zipPaste })
        : JSON.stringify({ category, offer_name: trimmed, zip_paste: zipPaste });
      const res = await call(path, { method, body });
      const unique = res.unique_count ?? zipCount;
      const dupes = res.duplicates_removed ?? 0;
      const dupText = dupes > 0
        ? ` · removed ${dupes.toLocaleString()} duplicate${dupes === 1 ? "" : "s"}`
        : "";
      const verb = isEdit ? "Updated" : "Created";
      onClose(`${verb} "${trimmed}" — saved ${unique.toLocaleString()} unique zip${unique === 1 ? "" : "s"}${dupText}.`);
    } catch (e) {
      setErr(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-sm px-4" data-testid="zip-offer-editor">
      <div className="bg-slate-900 border border-slate-800 rounded-xl w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-800 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${cat.bg} border ${cat.border}`}>
              <MapPin className={`w-4 h-4 ${cat.text}`} strokeWidth={2.25} />
            </div>
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">
                {isEdit ? "Edit offer" : "New offer"} · {cat.label}
              </p>
              <h2 className="text-[16px] font-bold text-white truncate">
                {isEdit ? (name || "…") : "Add New Offer"}
              </h2>
            </div>
          </div>
          <button type="button" onClick={() => onClose()} data-testid="zip-offer-editor-close"
            className="p-1.5 rounded-md text-slate-400 hover:text-white hover:bg-slate-800 transition">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="py-12 flex items-center justify-center text-slate-500 text-[13px]">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading offer…
            </div>
          ) : (
            <>
              <label className="block text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400 mb-2">
                Offer name
              </label>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="HVAC Repair" data-testid="zip-offer-name-input"
                className="w-full px-3 py-2.5 rounded-lg bg-slate-950/70 border border-slate-800 text-[13px] text-slate-100 placeholder-slate-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition mb-5"
              />

              <div className="flex items-center justify-between mb-2">
                <label className="block text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">
                  Zip Code Paste Pad
                </label>
                <span className="text-[11px] font-semibold text-slate-400 tabular-nums" data-testid="zip-offer-live-count">
                  {zipCount.toLocaleString()} unique
                  {dupCount > 0 && (
                    <span className="text-amber-300 ml-1">· {dupCount.toLocaleString()} dup{dupCount === 1 ? "" : "s"} will drop</span>
                  )}
                </span>
              </div>
              <textarea
                value={zipPaste} onChange={(e) => setZipPaste(e.target.value)}
                spellCheck={false}
                placeholder={"Paste up to 50,000 zip codes here.\nSeparated by commas, spaces, tabs or new lines.\nDuplicates are dropped automatically.\n4-digit zips (like 8021) are padded to 08021."}
                data-testid="zip-offer-paste-pad"
                rows={16}
                className="w-full px-3 py-2.5 rounded-lg bg-slate-950/70 border border-slate-800 text-[12px] text-slate-100 placeholder-slate-600 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition font-mono resize-y"
              />
              <p className="text-[11px] text-slate-500 mt-2">
                Handles up to 50k zips. Duplicates auto-removed. 4-digit zips auto-padded to 5.
              </p>

              {err && (
                <div className="mt-4 flex items-start gap-2 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-300">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{err}</span>
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-6 py-4 border-t border-slate-800 flex items-center justify-end gap-2 bg-slate-950/50">
          <button type="button" onClick={() => onClose()} disabled={saving}
            className="px-4 py-2 rounded-lg border border-slate-700 hover:border-slate-500 text-slate-200 hover:text-white text-[12.5px] font-semibold transition disabled:opacity-60">
            Cancel
          </button>
          <button type="button" onClick={save} disabled={saving || loading || !name.trim()}
            data-testid="zip-offer-save-btn"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r ${cat.swatch} hover:brightness-110 text-white text-[12.5px] font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            {isEdit ? "Save Changes" : "Create Offer"}
          </button>
        </div>
      </div>
    </div>
  );
}


function PreviewButton({ offerName, category }) {
  const [open, setOpen] = useState(false);
  const [zips, setZips] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    if (zips) { setOpen(true); return; }
    setLoading(true);
    try {
      const q = category ? `?category=${category}` : "";
      const data = await call(`/api/target-zips/${encodeURIComponent(offerName)}${q}`);
      setZips(data.zip_codes || []); setOpen(true);
    } catch { setZips([]); setOpen(true); }
    finally { setLoading(false); }
  };

  const copy = async () => {
    if (!zips) return;
    try {
      await navigator.clipboard.writeText(zips.join("\n"));
      setCopied(true); setTimeout(() => setCopied(false), 1600);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <>
      <button type="button" onClick={load} disabled={loading}
        data-testid={`zip-preview-${offerName}`}
        className="text-[11.5px] font-semibold px-2.5 py-1 rounded-md border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 transition disabled:opacity-60">
        {loading ? "Loading…" : "Preview"}
      </button>
      {open && zips && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" onClick={() => setOpen(false)}>
          <div className="bg-slate-900 border border-slate-800 rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[13px] text-slate-400 uppercase tracking-widest">Zip preview</p>
                <p className="text-[15px] font-bold text-white truncate">{offerName}</p>
              </div>
              <button onClick={copy} className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-md border border-slate-700 text-slate-200 hover:text-white hover:border-slate-500 transition">
                <Copy className="w-3.5 h-3.5" /> {copied ? "Copied" : "Copy all"}
              </button>
            </div>
            <div className="p-5 overflow-y-auto">
              <p className="text-[11.5px] text-slate-500 mb-2">{zips.length.toLocaleString()} zip codes</p>
              <div className="font-mono text-[12.5px] text-slate-200 leading-6 break-words whitespace-pre-wrap">
                {zips.join(", ")}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-800 flex justify-end">
              <button onClick={() => setOpen(false)} className="text-[12px] font-semibold px-3 py-1.5 rounded-md border border-slate-700 text-slate-300 hover:text-white hover:border-slate-500 transition">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
